import { Boss } from './boss-base';
import { getTexture } from '@/assets/resolver';
import {
  BURST_COUNT, BURST_MIN_TIER, CROSS_INTERVAL, CROSS_OFFSETS, DIVE_INTERVAL, DIVE_SPEED,
  DIVE_TIME, DRIP_INTERVAL, ENRAGE_MUL, FAN_NARROW, FAN_WIDE, FIRE_INTERVAL, HEAD_RADIUS,
  PATROL_RADIUS, PATROL_SPEED, SEG_COUNT, SERPENT, SHOT_SPEED, SLITHER_AMP, SLITHER_FREQ,
  SPEED, TRAIL_INTERVAL,
  WIDE_FAN_TIER, WINDUP, makeSnake, snakeTouches, steerSnake, type Snake,
} from './serpent-parts';
export { SERPENT } from './serpent-parts';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

/**
 * B4 — Twin Serpents (void flavor). Two bullet-snakes: segments are
 * INVULNERABLE (bullets pass through), only the heads take damage.
 * They SLITHER in counter-rotating weaving loops, periodically wind up (head
 * flashes red — in-fiction telegraph) and LUNGE at the player while the
 * trail drips mine-like bullets. When one dies the other ENRAGES.
 * (Centipede burrows, Broodworm coils — docs/02 B2 keeps the chains apart.)
 * docs/02-bosses.md
 */
export class SerpentBoss extends Boss {
  private snakes: Snake[] = [];
  private time = 0;
  private enraged = false;
  private crossTimer: number = CROSS_INTERVAL * 0.7;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(SERPENT, level, x, y, homeX, homeY);
    this.body.visible = false; // fully custom rendering
    this.snakes = [
      makeSnake(this.view, 0, 0, 1, homeX, homeY),
      makeSnake(this.view, 0, Math.PI, -1, homeX, homeY),
    ];
    this.splitHp();
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  /** Two equal pools — re-split AFTER power scaling (X1). */
  private splitHp(): void {
    const half = Math.round(this.maxHp / 2);
    this.snakes[0]!.hp = half;
    this.snakes[1]!.hp = this.maxHp - half;
  }

  /** Movement ramp: enrage × tier move multiplier */
  private get speedFactor(): number {
    return (this.enraged ? ENRAGE_MUL : 1) * this.levelSpeedMul;
  }

  /** Fire ramp: enrage × tier tempo */
  private get fireFactor(): number {
    return (this.enraged ? ENRAGE_MUL : 1) * this.intensity;
  }

  /** Power scaling hook: re-split the scaled maxHp across both snakes. */
  protected override scaleParts(_mul: number): void {
    this.splitHp();
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.updateReinforce(dt);

    for (const s of this.snakes) {
      if (!s.alive) continue;
      this.updateSnake(s, dt, px, py, bullets);
    }

    // Cross volley (J3): while BOTH snakes live they sync a pincer shot —
    // two converging pairs from opposite sides of the arena
    const both = this.snakes[0]!.alive && this.snakes[1]!.alive;
    if (both) {
      this.crossTimer -= dt * this.intensity;
      if (this.crossTimer <= 0) {
        this.crossTimer = CROSS_INTERVAL;
        for (const s of this.snakes) {
          const base = Math.atan2(py - s.head.y, px - s.head.x);
          for (const off of CROSS_OFFSETS) this.shoot(bullets, s, base + off, SHOT_SPEED.cross);
        }
      }
    }

    // Boss reference point (homing, loot, evolve layers) = first living head
    const lead = this.snakes.find((s) => s.alive);
    if (lead) {
      this.x = lead.head.x;
      this.y = lead.head.y;
    }
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  private shoot(bullets: BulletSystem, s: Snake, angle: number, speed: number): void {
    bullets.spawn({
      x: s.head.x, y: s.head.y, dirX: Math.cos(angle), dirY: Math.sin(angle),
      speed: speed * this.bulletSpeedMul, life: 4, radius: 10, damage: 1,
      friendly: false, texKey: 'bullet/enemy/ball', rotates: false,
    });
  }

  private updateSnake(s: Snake, dt: number, px: number, py: number, bullets: BulletSystem): void {
    const f = this.speedFactor;
    const head = s.head;
    head.prevX = head.x;
    head.prevY = head.y;

    // Movement: patrol loop → wind-up (red flash) → dive at captured point
    let targetX: number;
    let targetY: number;
    let maxSpeed = SPEED * f;

    if (s.diving > 0) {
      s.diving -= dt;
      targetX = s.diveX;
      targetY = s.diveY;
      maxSpeed = DIVE_SPEED * f;
      // Dive wake (J3): slow mines pepper the path while it lunges
      s.trailTimer -= dt;
      if (s.trailTimer <= 0) {
        s.trailTimer = TRAIL_INTERVAL;
        const v = Math.hypot(s.vx, s.vy) || 1;
        bullets.spawn({
          x: head.x, y: head.y, dirX: -s.vx / v, dirY: -s.vy / v,
          speed: 36, life: 2.6, radius: 9, damage: 1,
          friendly: false, texKey: 'bullet/enemy/ring', rotates: false,
        });
      }
      // Pull-out burst (J3): deeper tiers end the dive with a radial pop
      if (s.diving <= 0 && this.tier >= BURST_MIN_TIER) {
        for (let i = 0; i < BURST_COUNT; i++) {
          this.shoot(bullets, s, (Math.PI * 2 * i) / BURST_COUNT, SHOT_SPEED.burst);
        }
      }
    } else if (s.windup > 0) {
      s.windup -= dt;
      targetX = head.x;
      targetY = head.y;
      if (s.windup <= 0) {
        s.diveX = px;
        s.diveY = py;
        s.diving = DIVE_TIME;
      }
    } else {
      s.phase += PATROL_SPEED * f * s.dir * dt;
      // Slither: the loop radius breathes in and out, so the chain S-curves
      const r = PATROL_RADIUS + Math.sin(this.time * SLITHER_FREQ + s.phase * 2) * SLITHER_AMP;
      targetX = this.homeX + Math.cos(s.phase) * r;
      targetY = this.homeY + Math.sin(s.phase) * r * 0.7;
      s.diveTimer -= dt;
      if (s.diveTimer <= 0) {
        s.diveTimer = DIVE_INTERVAL / f;
        s.windup = WINDUP;
      }
    }

    steerSnake(s, targetX, targetY, maxSpeed, dt);

    // Head: aimed fan — the enraged survivor (J3) and deep tiers go wide
    s.fireTimer -= dt;
    if (s.fireTimer <= 0) {
      s.fireTimer = FIRE_INTERVAL / this.fireFactor;
      const base = Math.atan2(py - head.y, px - head.x);
      const offs = this.enraged || this.tier >= WIDE_FAN_TIER ? FAN_WIDE : FAN_NARROW;
      for (const off of offs) this.shoot(bullets, s, base + off, SHOT_SPEED.head);
    }

    // Trail drip: cycling segment leaves a slow mine behind
    s.dripTimer -= dt;
    if (s.dripTimer <= 0) {
      s.dripTimer = DRIP_INTERVAL / this.fireFactor;
      s.dripIndex = (s.dripIndex + 3) % SEG_COUNT;
      const seg = s.segs[s.dripIndex]!;
      const away = Math.atan2(seg.y - this.homeY, seg.x - this.homeX) + 0.6;
      bullets.spawn({
        x: seg.x, y: seg.y,
        dirX: Math.cos(away), dirY: Math.sin(away),
        speed: 32, life: 3.6, radius: 10, damage: 1,
        friendly: false, texKey: 'bullet/enemy/ring', rotates: false,
      });
    }
  }

  // ---- Custom collision: only living heads are vulnerable ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const s of this.snakes) {
      if (!s.alive) continue;
      hits += bullets.collideCircle(true, s.head.x, s.head.y, HEAD_RADIUS, (dmg) => {
        const taken = this.hurtHead(s, dmg);
        dealt += taken;
        return taken; // the number shows what this head really lost
      });
    }
    return { hits, dealt };
  }

  /** Nearest living head — auto-aim + homing follow the real target (X7). */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    let best: { x: number; y: number } = this;
    let bestD = Infinity;
    for (const s of this.snakes) {
      if (!s.alive) continue;
      const d = (s.head.x - px) ** 2 + (s.head.y - py) ** 2;
      if (d < bestD) {
        bestD = d;
        best = s.head;
      }
    }
    return best;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return this.blastHead(x, y, radius) !== null;
  }

  /** Explosions hurt the head INSIDE the blast (X5), never the other one. */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    const s = this.blastHead(x, y, radius);
    if (!s) return 0;
    this.markAoe(s.head.x, s.head.y, HEAD_RADIUS);
    return this.hurtHead(s, damage);
  }

  override takeDamage(amount: number): boolean {
    const s = this.snakes.find((n) => n.alive);
    if (s) this.hurtHead(s, amount);
    return this.dead;
  }

  private blastHead(x: number, y: number, radius: number): Snake | null {
    let best: Snake | null = null;
    let bestD = radius + HEAD_RADIUS;
    for (const s of this.snakes) {
      if (!s.alive) continue;
      const d = Math.hypot(s.head.x - x, s.head.y - y);
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  }

  /** @returns damage dealt (0 while the reinforcement squad shields it) */
  private hurtHead(s: Snake, amount: number): number {
    if (this.dead || !s.alive || this.damageLocked) return 0;
    const dealt = Math.min(s.hp, amount);
    s.hp -= amount;
    if (s.hp <= 0) this.killSnake(s);
    this.hp = Math.max(0, this.snakes.reduce((sum, n) => sum + (n.alive ? n.hp : 0), 0));
    if (this.snakes.every((n) => !n.alive)) this.dead = true;
    return dealt;
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return this.snakes.some((s) => s.alive && snakeTouches(s, px, py, playerRadius));
  }

  private killSnake(s: Snake): void {
    if (!s.alive) return;
    s.alive = false;
    const points = [s.head, ...s.segs].map((n) => ({ x: n.x, y: n.y }));
    s.head.sprite.visible = false;
    for (const seg of s.segs) seg.sprite.visible = false;
    this.events?.chain(points, BOSS_FEEDBACK.serpentDeath);
    if (this.snakes.some((o) => o.alive)) this.enraged = true;
  }

  override render(alpha: number): void {
    this.view.position.set(0, 0);
    for (const s of this.snakes) {
      if (!s.alive) continue;
      const head = s.head;
      head.sprite.position.set(
        head.prevX + (head.x - head.prevX) * alpha,
        head.prevY + (head.y - head.prevY) * alpha,
      );
      head.sprite.rotation = Math.atan2(s.vy, s.vx);
      head.sprite.texture = getTexture('boss/serpent/head', this.enraged ? 1 : 0);
      // In-fiction dive telegraph: head flashes red during wind-up
      head.sprite.tint = s.windup > 0 && Math.sin(this.time * 40) > 0 ? 0xff6655 : this.levelTint;

      for (const seg of s.segs) {
        seg.sprite.position.set(
          seg.prevX + (seg.x - seg.prevX) * alpha,
          seg.prevY + (seg.y - seg.prevY) * alpha,
        );
      }
    }
  }
}
